"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { partitionFiles, resolveMimeType } from "./validate";
import { uploadAttachment } from "./upload";
import type { AttachmentValidationError, PendingAttachment } from "./types";

/**
 * Hook that manages a tray of attachment files for the SOLA chat input.
 *
 * - Validates files client-side (mime + size).
 * - Generates `previewUrl` object URLs for images so the chip can render
 *   a thumbnail while the upload is in flight.
 * - Uploads each file to /api/sola/uploads in parallel; UI updates as
 *   each one settles.
 * - `clear()` revokes outstanding object URLs and aborts in-flight uploads.
 */
export function useAttachments({
  conversationId,
}: {
  conversationId?: string | null;
}) {
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [errors, setErrors] = useState<AttachmentValidationError[]>([]);
  const abortRefs = useRef<Map<string, AbortController>>(new Map());

  // Revoke all object URLs when the hook unmounts.
  useEffect(() => {
    const abortControllers = abortRefs.current;
    return () => {
      abortControllers.forEach((c) => c.abort());
    };
  }, []);

  const add = useCallback(
    (incoming: File[] | FileList) => {
      const list = Array.isArray(incoming) ? incoming : Array.from(incoming);
      const { ok, rejected } = partitionFiles(list);

      if (rejected.length > 0) {
        setErrors((prev) => [...prev, ...rejected]);
      }

      const newRows: PendingAttachment[] = ok.map((file) => {
        const localId =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const mediaType = resolveMimeType(file);
        return {
          localId,
          filename: file.name,
          mediaType,
          size: file.size,
          previewUrl: mediaType.startsWith("image/")
            ? URL.createObjectURL(file)
            : null,
          status: "uploading",
        };
      });

      if (newRows.length === 0) {
        return;
      }

      setPending((prev) => [...prev, ...newRows]);

      newRows.forEach((row, idx) => {
        const file = ok[idx];
        const controller = new AbortController();
        abortRefs.current.set(row.localId, controller);

        uploadAttachment(file, { conversationId, signal: controller.signal })
          .then((uploaded) => {
            setPending((prev) =>
              prev.map((p) =>
                p.localId === row.localId
                  ? { ...p, status: "uploaded" as const, uploaded }
                  : p
              )
            );
          })
          .catch((err: unknown) => {
            if (controller.signal.aborted) {
              return;
            }
            const message = err instanceof Error ? err.message : "Upload failed";
            setPending((prev) =>
              prev.map((p) =>
                p.localId === row.localId
                  ? { ...p, status: "error" as const, error: message }
                  : p
              )
            );
          })
          .finally(() => {
            abortRefs.current.delete(row.localId);
          });
      });
    },
    [conversationId]
  );

  const remove = useCallback((localId: string) => {
    abortRefs.current.get(localId)?.abort();
    abortRefs.current.delete(localId);

    setPending((prev) => {
      const target = prev.find((p) => p.localId === localId);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((p) => p.localId !== localId);
    });
  }, []);

  const clear = useCallback(() => {
    abortRefs.current.forEach((c) => c.abort());
    abortRefs.current.clear();
    setPending((prev) => {
      prev.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setErrors([]);
  }, []);

  const dismissError = useCallback((index: number) => {
    setErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const isUploading = pending.some((p) => p.status === "uploading");
  const hasReady = pending.some((p) => p.status === "uploaded");

  return {
    pending,
    errors,
    add,
    remove,
    clear,
    dismissError,
    isUploading,
    hasReady,
  } as const;
}

export type UseAttachmentsReturn = ReturnType<typeof useAttachments>;
